import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Card,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  LinearProgress,
  TableContainer,
  TableSortLabel
} from '@material-ui/core';
import { AssignmentReturned as AssignmentReturnedIcon } from '@material-ui/icons';

import { ApiParams } from '../../../common/api/api';
import CardTitle from '../../../common/card/CardTitle';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import WrappedTableCell from '../../../common/WrappedTableCell';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import {
  useFindProductDesignInputsByVerificationId,
  useProductEntityPrefix
} from '../Product.hooks';
import {
  ProductDesignInput,
  BaseProductVerificationCardProps
} from '../Product.interface';
import {
  getProductEntityName,
  isProductRelationDisabled
} from '../Product.config';

export interface VerificationDesignInputReferenceCardProps
  extends BaseProductVerificationCardProps {
  fetchRef?: React.MutableRefObject<Function | undefined>;
}

const VerificationDesignInputReferenceCard: React.FC<VerificationDesignInputReferenceCardProps> =
  (props: VerificationDesignInputReferenceCardProps) => {
    const {
      productId,
      productVerification,
      productVerificationVersion,
      fetchRef,
      onError
    } = props;
    const history = useHistory();
    const [order, setOrder] = useState<string | undefined>();
    const [orderDirection, setOrderDirection] = useState<
      'ASC' | 'DESC' | undefined
    >();
    const {
      productDesignInputList,
      isLoadingProductDesignInputs,
      loadingProductDesignInputsError,
      findProductDesignInputs
    } = useFindProductDesignInputsByVerificationId(
      productId,
      productVerification.id
    );
    const prefix: string = useProductEntityPrefix(
      ProductBoardColumnEntity.DesignInput
    );

    const handleSort = (column: string) => {
      setOrder(column);
      setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
    };

    const getSortDirection = (column: string): 'asc' | 'desc' => {
      return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
    };

    const findProductDesignInputsWithParams = useCallback(() => {
      const params: ApiParams = {};
      if (order) params.order = order;
      if (orderDirection) params.direction = orderDirection;
      if (productVerificationVersion)
        params.version = productVerificationVersion.version;
      params.getFromRelation = !isProductRelationDisabled(
        'designOutput',
        'verifications'
      );
      findProductDesignInputs(params);
    }, [
      findProductDesignInputs,
      order,
      orderDirection,
      productVerificationVersion
    ]);

    useEffect(() => {
      if (fetchRef) {
        fetchRef.current = findProductDesignInputsWithParams;
      }
      findProductDesignInputsWithParams();
    }, [fetchRef, findProductDesignInputsWithParams]);

    useEffect(() => {
      if (loadingProductDesignInputsError) {
        onError?.(loadingProductDesignInputsError);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingProductDesignInputsError]);

    return (
      <Card>
        <CardTitle>
          <AssignmentReturnedIcon />
          {getProductEntityName('designInput')} Reference
        </CardTitle>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={order === 'productDesignInput.id'}
                    direction={getSortDirection('productDesignInput.id')}
                    onClick={() => handleSort('productDesignInput.id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'productDesignInput.title'}
                    direction={getSortDirection('productDesignInput.title')}
                    onClick={() => handleSort('productDesignInput.title')}
                  >
                    Title
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'task.status'}
                    direction={getSortDirection('task.status')}
                    onClick={() => handleSort('task.status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingProductDesignInputs && (
                <TableRow>
                  <TableCell colSpan={2} padding='none'>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {productDesignInputList?.map(
                (designInput: ProductDesignInput) => (
                  <TableRow
                    hover
                    key={designInput.id}
                    onClick={() => {
                      history.push(
                        `/portal/products/${productId}/design-inputs/${designInput.id}`
                      );
                    }}
                  >
                    <TableCell>{prefix + designInput.id}</TableCell>
                    <WrappedTableCell>{designInput.title}</WrappedTableCell>
                    <TableCell>
                      <TaskStatusLabel task={designInput.task} />
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    );
  };

export default VerificationDesignInputReferenceCard;
